var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Button, Loader, Menu } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { DocumentMetadataPropertyReferenceEnum, DocumentMetadataPropertyUiWidgetHintEnum, } from 'shared/domains/apollo/generated/types';
import { SearchableMenu, SearchableMenuMultiSelectList } from 'shared/domains/common-ui/SearchableMenu';
import { BooleanMenu } from './BooleanMenu';
import { BulkSetMetadataDocument } from './BulkSetMetadata.gql';
import { DateMenu } from './DateMenu';
import { InputText } from './InputText';
import { ListMenu } from './ListMenu';
import { NoResults } from './NoResults';
import { NumberMenu } from './NumberMenu';
import { ProcessingBulkUpdateDialog } from './ProcessingBulkUpdateDialog';
import { SelectWithInput } from './SelectWithInput';
import { UserMenu } from './UserMenu';
import { getMetadataPropertyInitialValue } from './getMetadataPropertyInitialValue';
import { useBulkUpdateMetadataProperty } from './useBulkUpdateMetadataProperty';
import { useTableContext } from '../../SearchResultsTable/TableContext';
const BULK_ACTIONS_LIMIT = 100;
export function BulkSetMetadata() {
    const { selected } = useTableContext();
    const documentIds = selected.map((item) => item.entityId);
    const { data, error, loading: queryLoading, refetch } = useQuery(BulkSetMetadataDocument);
    const [menuOpen, setMenuOpen] = useState(false);
    const hideMenu = () => {
        setMenuOpen(false);
    };
    const toggleMenu = () => setMenuOpen(!menuOpen);
    const { pollingState, setMetadataProperty, clearMetadataProperty, loading } = useBulkUpdateMetadataProperty();
    const showLoader = loading || pollingState.state === 'polling' || queryLoading;
    const buttonDisabled = selected.length > BULK_ACTIONS_LIMIT || showLoader;
    const { metadataCategories } = data || {};
    const setMetadata = (property, value) => __awaiter(this, void 0, void 0, function* () {
        setMetadataProperty({ documentIds, property, value });
        hideMenu();
    });
    const clearMetadata = (property) => __awaiter(this, void 0, void 0, function* () {
        clearMetadataProperty({ documentIds, property });
        hideMenu();
    });
    const renderPropertyMenu = (property) => {
        const { reference, uiWidgetHint } = property;
        if (uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.SelectWithInput) {
            return (<SelectWithInput key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.Select) {
            if (reference === DocumentMetadataPropertyReferenceEnum.User) {
                return (<UserMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
            }
            return (<ListMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.InputNumber) {
            return (<NumberMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.Switch) {
            return (<BooleanMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.DatePicker ||
            uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.DateTimePicker) {
            return (<DateMenu key={property.name} property={property} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        if (uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.InputText ||
            uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.TextArea) {
            return (<InputText key={property.name} property={property} multiline={uiWidgetHint === DocumentMetadataPropertyUiWidgetHintEnum.TextArea} onChange={setMetadata} onClear={clearMetadata} initialValue={getMetadataPropertyInitialValue(selected, property)}/>);
        }
        return (<Menu.Item disabled>
        <Menu.ItemText primary={property.label} secondary={t({
                message: 'Unsupported for bulk actions',
                comment: 'Displayed in bulk actions if a type of metadata is unsupported',
            })}/>
      </Menu.Item>);
    };
    const handleToggleMenu = () => __awaiter(this, void 0, void 0, function* () {
        if (error) {
            return refetch()
                .then(toggleMenu)
                .catch((e) => e);
        }
        return toggleMenu();
    });
    return (<Fragment>
      {pollingState.state === 'polling' && <ProcessingBulkUpdateDialog {...pollingState}/>}

      <SearchableMenu keys={['label']} items={(data === null || data === void 0 ? void 0 : data.documentMetadataProperties) || []} groups={metadataCategories} itemGroupKey="categoryId" placeholder={t({
            message: 'Find metadata property...',
            comment: 'Bulk action: Metadata search input placeholder',
        })} renderTarget={<Button size="small" appearance="glass" disabled={buttonDisabled || !!error} onClick={handleToggleMenu}>
            {showLoader ? <Loader /> : <Falcon icon="tag"/>}
            <Trans comment="Bulk action: Metadata">Metadata</Trans>
          </Button>}>
        <SearchableMenuMultiSelectList flat={false} renderListWrapper={false} renderItem={renderPropertyMenu} emptyComponent={<NoResults>
              <Trans comment="Bulk action: Metadata - No properties found">No properties found</Trans>
            </NoResults>}/>
      </SearchableMenu>
    </Fragment>);
}
